import { useState } from "react";
import { toast } from "sonner";
import { Plus, PencilSimple, Trash, AddressBook, Phone, EnvelopeSimple } from "@phosphor-icons/react";
import { api, errMsg } from "@/lib/api";
import { useGet } from "@/hooks/useResource";
import { Loading, PageHeader, EmptyState } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";

const BLANK = { name: "", category: "", contact: "", phone: "", notes: "" };
const PATH = "/client/supplier-contacts";

export default function Suppliers() {
  const { data: items, loading, refetch } = useGet(PATH);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(BLANK);
  const [deleteId, setDeleteId] = useState(null);
  const list = items || [];

  const openAdd = () => { setEditing(null); setForm(BLANK); setOpen(true); };
  const openEdit = (s) => { setEditing(s); setForm({ ...BLANK, ...s }); setOpen(true); };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Supplier name is required."); return; }
    try {
      if (editing) await api.put(`${PATH}/${editing.id}`, form);
      else await api.post(PATH, form);
      toast.success(editing ? "Supplier updated" : "Supplier added");
      setOpen(false); refetch();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const confirmDelete = async () => {
    try { await api.delete(`${PATH}/${deleteId}`); toast.success("Supplier removed"); setDeleteId(null); refetch(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  if (loading) return <Loading label="Loading suppliers" />;

  return (
    <div>
      <PageHeader eyebrow="Your Event" title="My Suppliers" description="Keep your own supplier contacts in one place — photographer, florist, celebrant and more.">
        <Button className="rounded-full bg-primary hover:bg-primary/90" onClick={openAdd} data-testid="add-supplier-btn"><Plus size={18} className="mr-1" /> Add supplier</Button>
      </PageHeader>

      {list.length === 0 ? (
        <EmptyState icon={AddressBook} title="No suppliers yet" description="Add your booked suppliers so all their contact details are handy."
          action={<Button className="rounded-full bg-primary" onClick={openAdd}>Add supplier</Button>} />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {list.map((s) => (
            <div key={s.id} data-testid={`supplier-card-${s.id}`} className="group rounded-2xl border border-border bg-card p-6 transition-transform duration-200 hover:-translate-y-1 hover:shadow-lg">
              <div className="flex items-start justify-between">
                <div>{s.category && <p className="eyebrow text-accent">{s.category}</p>}<h3 className="font-heading text-xl text-primary mt-1">{s.name}</h3></div>
                <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                  <button onClick={() => openEdit(s)} data-testid={`edit-supplier-${s.id}`} className="p-1.5 rounded-lg text-muted-foreground hover:text-primary hover:bg-muted transition-colors duration-200"><PencilSimple size={16} /></button>
                  <button onClick={() => setDeleteId(s.id)} data-testid={`delete-supplier-${s.id}`} className="p-1.5 rounded-lg text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors duration-200"><Trash size={16} /></button>
                </div>
              </div>
              <div className="mt-4 space-y-2 text-sm text-muted-foreground">
                {s.contact && <p className="flex items-center gap-2"><EnvelopeSimple size={16} /> {s.contact}</p>}
                {s.phone && <p className="flex items-center gap-2"><Phone size={16} /> {s.phone}</p>}
                {s.notes && <p className="pt-2 text-foreground/80">{s.notes}</p>}
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="font-heading text-2xl font-light">{editing ? "Edit supplier" : "Add supplier"}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div><Label className="eyebrow">Name</Label><Input className="mt-2" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="supplier-name-input" /></div>
            <div><Label className="eyebrow">Category</Label><Input className="mt-2" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} placeholder="e.g. Photography" /></div>
            <div><Label className="eyebrow">Email / contact</Label><Input className="mt-2" value={form.contact} onChange={(e) => setForm({ ...form, contact: e.target.value })} /></div>
            <div><Label className="eyebrow">Phone</Label><Input className="mt-2" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
            <div><Label className="eyebrow">Notes</Label><Textarea className="mt-2" rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => setOpen(false)}>Cancel</Button>
            <Button className="rounded-full bg-primary hover:bg-primary/90" onClick={save} data-testid="save-supplier-btn">{editing ? "Save changes" : "Add supplier"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Remove this supplier?</AlertDialogTitle><AlertDialogDescription>This removes the contact from your list.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full">Cancel</AlertDialogCancel>
            <AlertDialogAction className="rounded-full bg-destructive hover:bg-destructive/90" onClick={confirmDelete} data-testid="confirm-delete-supplier">Remove</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
